import moment from "moment";
import * as Sentry from '@sentry/browser';

export class GATracker {
    constructor() {
        this.providerGAId = null;
    }

    setProviderGAId(providerGAId) {
        this.providerGAId = providerGAId;
    }

    track(eventConstructor) {
        if (typeof eventConstructor !== 'function') {
            console.error('arg should be a function');
            return;
        }
        try {
            const event = eventConstructor();
            if (this.providerGAId) {
                if (event.providerVisible) {
                    window[`ga-disable-${this.providerGAId}`] = false;
                } else {
                    window[`ga-disable-${this.providerGAId}`] = true;
                }
            }

            window.gtag('event', event.name, event.parameters);
        } catch (error) {
            Sentry.captureException(error);
        }
    }
}

export class ConsoleTracker {
    track(event, providerGAId) {
        if (event.providerVisible && providerGAId) {
            console.log('add provider GA-Id: ' + providerGAId);
        }

        console.log(`Processing GTAG command: ["event", ${event.name}, ${event.parameters}]`)
    }
}


// Provider ---------------------------------------------------------------------------------------------------
// Dashboard
export class EventSwitchDashboardPeriod {
    constructor(currentPeriod) {
        this.name = 'switch_dashboard_period';
        this.providerVisible = false;
        this.parameters = {
            current_period: currentPeriod
        };
    }
}

// Notification
export class EventMarkAllNotificationRead {
    constructor() {
        this.name = 'mark_all_notification_read';
        this.providerVisible = false;
        this.parameters = {};
    }
}

export class EventSwitchNotificationTab {
    constructor(currentTab) {
        this.name = 'switch_notification_tab';
        this.providerVisible = false;
        this.parameters = {
            current_tab: currentTab
        };
    }
}

// Schedule
export class EventAddCalendarEvent {
    constructor(eventType, serviceId, memberId, start, end, isSuccess) {
        this.name = 'add_calendar_event';
        this.providerVisible = false;
        start = moment(start).utc().format();
        end = moment(end).utc().format();
        const parameters = {
            event_type: eventType,
            start: start,
            end: end,
            is_success: isSuccess
        }
        if (serviceId) {
            parameters.service_id = serviceId;
        }
        if (memberId) {
            parameters.member_id = memberId;
        }
        this.parameters = parameters;
    }
}

export class EventRemoveCalendarEvent {
    constructor(openedEvent) {
        this.name = 'remove_calendar_event';
        this.providerVisible = false;

        const parameters = {
            start: moment(openedEvent.start).utc().format(),
            end: moment(openedEvent.end).utc().format()
        };
        if (openedEvent.type === 'leave') {
            parameters.event_type = 'member_leave';
            parameters.event_id = openedEvent.leaveId; 
        } else if (openedEvent.type === 'service_leave') {
            parameters.event_type = 'service_leave';
            parameters.event_id = openedEvent.serviceId;
        } else if (openedEvent.type === 'extra_hours') {
            parameters.event_type = 'extra_hours';
            parameters.event_id = openedEvent.extraHoursId;
        }
        this.parameters = parameters;
    }
}

//  Booking 
export class EventViewBookingSearchResults {
    constructor(keyword) {
        this.name = 'view_booking_search_results';
        this.providerVisible = false;
        this.parameters = {
            search_term: keyword
        };
    }
}

export class EventSwitchBookingTab {
    constructor(tab) {
        this.name = 'switch_booking_tab';
        this.providerVisible = false;
        this.parameters = {
            current_tab: tab
        };
    }
}

export class EventBeginSelectBookings {
    constructor() {
        this.name = 'begin_select_bookings';
        this.providerVisible = false;
        this.parameters = {};
    }
}

export class EventSelectAllBookings {
    constructor(selectedBookings) {
        this.name = 'select_all_bookings';
        this.providerVisible = false;
        this.parameters = {
            selected_bookings_count: selectedBookings.map(booking => booking.id).length
        };
    }
}

export class EventPrintSelectedBookings {
    constructor(selectedBookings) {
        this.name = 'print_selected_bookings';
        this.providerVisible = false;
        this.parameters = {
            selected_bookings_count: selectedBookings.map(booking => booking.id).length
        };
    }
}

export class EventExportSelectedBookings {
    constructor(selectedBookings) {
        this.name = 'export_selected_bookings';
        this.providerVisible = false;
        this.parameters = {
            selected_bookings_count: selectedBookings.map(booking => booking.id).length
        };
    }
}

export class EventPrintFilteredBookings {
    constructor(filteredBookings) {
        this.name = 'print_filtered_bookings';
        this.providerVisible = false;
        this.parameters = {
            filtered_bookings_count: filteredBookings.map(booking => booking.id).length
        };
    }
}

export class EventExportFilteredBookings {
    constructor() {
        this.name = 'export_filtered_bookings';
        this.providerVisible = false;
        this.parameters = {};
    }
}

export class EventExportTCATFilteredBookings {
    constructor() {
        this.name = 'export_tcat_filtered_bookings';
        this.providerVisible = false;
        this.parameters = {};
    }
}

export class EventClickBookingMoreAction {
    constructor(booking, type) {
        this.name = 'click_booking_more_action';
        this.providerVisible = false;
        this.parameters = {
            booking_id: booking.id,
            booking_status: booking.status,
            more_action_type: type
        };
    }
}

export class EventSaveBooking {
    constructor(booking, isSuccess) {
        this.name = 'save_booking';
        this.providerVisible = false;
        this.parameters = {
            booking_id: booking.id,
            save_method: booking.id ? 'update' : 'create',
            booking_status: booking.status,
            is_success: isSuccess
        };
    }
}

export class EventUpdateBookingNote {
    constructor(booking) {
        this.name = 'update_booking_note';
        this.providerVisible = false;
        this.parameters = {
            booking_id: booking.id,
            save_method: booking.id ? 'update' : 'create',
            booking_status: booking.status
        };
    }
}

export class EventUpdateNoteToCustomer {
    constructor(booking) {
        this.name = 'update_note_to_customer';
        this.providerVisible = false;
        this.parameters = {
            booking_id: booking.id,
            save_method: booking.id ? 'update' : 'create',
            booking_status: booking.status
        };
    }
}

export class EventEnableBookingMakeUpMode {
    constructor(booking) {
        this.name = 'enable_booking_make_up_mode';
        this.providerVisible = false;
        this.parameters = {
            booking_id: booking.id,
            save_method: booking.id ? 'update' : 'create',
            booking_status: booking.status
        };
    }
}

export class EventCreateFollowUpService {
    constructor(booking) {
        this.name = 'create_follow_up_service';
        this.providerVisible = false;
        this.parameters = {
            booking_id: booking.id,
            customer_id: booking.customer_id,
            booking_status: booking.status
        };
    }
}

export class EventCancelBooking {
    constructor(booking) {
        this.name = 'cancel_booking';
        this.providerVisible = false;
        this.parameters = {
            booking_id: booking.id,
            customer_id: booking.customer_id
        };
    }
}

export class EventViewBookingNotification {
    constructor(booking) {
        this.name = 'view_booking_notification';
        this.providerVisible = false;
        this.parameters = {
            booking_id: booking.id,
            booking_status: booking.status
        };
    }
}

// Payment Module
export class EventAddPayment {
    constructor(payment, currency) {
        this.name = 'add_payment';
        this.providerVisible = false;
        this.parameters = {
            booking_id: payment.booking_id,
            payment_id: payment.id,
            value: payment.amount,
            gateway_name: payment.gateway_name,
            payment_status: payment.status,
            currency: currency
        };
    }
}

export class EventMarkPaymentPaid {
    constructor(payment, currency) {
        this.name = 'mark_payment_paid';
        this.providerVisible = false;
        this.parameters = {
            booking_id: payment.booking_id,
            payment_id: payment.id,
            value: payment.amount,
            gateway_name: payment.gateway_name,
            currency: currency
        };
    }
}

export class EventDeletePayment {
    constructor(payment) {
        this.name = 'delete_payment';
        this.providerVisible = false;
        this.parameters = {
            booking_id: payment.booking_id,
            payment_id: payment.id,
            gateway_name: payment.gateway_name,
            payment_status: payment.status
        };
    }
}

export class EventRequestPayment {
    constructor(payment, currency) {
        this.name = 'request_payment';
        this.providerVisible = false;

        var allowedGatewayNamesArray = [];
        if (payment.allowed_gateway_names) {
            allowedGatewayNamesArray = payment.allowed_gateway_names.split('|');
        } else if (payment.gateway_name) {
            allowedGatewayNamesArray.push(payment.gateway_name);
        }
        this.parameters = {
            currency: currency,
            booking_id: payment.booking_id,
            payment_id: payment.id,
            value: payment.amount,
            allowed_atm: !!allowedGatewayNamesArray.find(item => item == 'atm'),
            allowed_linepay: !!allowedGatewayNamesArray.find(item => item == 'linepay'),
            allowed_ecpay: !!allowedGatewayNamesArray.find(item => item == 'ecpay'),
            allowed_tappay: !!allowedGatewayNamesArray.find(item => item == 'tappay')
        };
    }
}

export class EventSwitchPaymentToManual {
    constructor(payment, currency) {
        this.name = 'switch_payment_to_manual';
        this.providerVisible = false;

        var allowedGatewayNamesArray = [];
        if (payment.allowed_gateway_names) {
            allowedGatewayNamesArray = payment.allowed_gateway_names.split('|');
        } else if (payment.gateway_name) {
            allowedGatewayNamesArray.push(payment.gateway_name);
        }
        this.parameters = {
            currency: currency,
            booking_id: payment.booking_id,
            payment_id: payment.id,
            value: payment.amount,
            payment_status: payment.status,
            allowed_linepay: !!allowedGatewayNamesArray.find(item => item == 'linepay'),
            allowed_ecpay: !!allowedGatewayNamesArray.find(item => item == 'ecpay')
        };
    }
}

//  Order Items
export class EventViewOrderItems {
    constructor(order, currency) {
        this.name = 'view_order_items';
        this.providerVisible = false;

        const items = order.items.map(item => {
            return {
                item_id: item.id,
                item_name: item.title,
                price: item.price,
                quantity: item.count,
                is_renting: item.is_renting
            }
        })
        this.parameters = {
            currency: currency,
            order_id: order.id,
            booking_id: order.booking_id,
            customer_id: order.customer_id,
            order_status: order.status,
            value: order.total,
            items: items
        };
    }
}

export class EventAddOrderItems {
    constructor(items, order, currency) {
        this.name = 'add_order_item';
        this.providerVisible = false;
        const itemsPayload = items.map(item => {
            return {
                item_id: item.orderable_id,
                item_name: item.title,
                price: item.price,
                quantity: item.count,
                is_renting: !!item.is_renting,
                item_type: item.orderable_type
            }
        })

        const total = items.reduce((total, item) => {
            return total + item.total
        }, 0)

        this.parameters = {
            currency: currency,
            value: total,
            items: itemsPayload,
            order_id: order.id,
            booking_id: order.booking_id,
            customer_id: order.customer_id,
            order_status: order.status
        };
    }
}

export class EventUpdateOrderItems {
    constructor(order, currency) {
        this.name = 'update_order_items';
        this.providerVisible = false;

        const items = order.items.map(item => {
            return {
                item_id: item.id,
                item_name: item.title,
                price: item.price,
                quantity: item.count,
                is_renting: item.is_renting
            }
        })
        this.parameters = {
            currency: currency,
            order_id: order.id,
            booking_id: order.booking_id,
            customer_id: order.customer_id,
            order_status: order.status,
            value: order.total,
            items: items
        };
    }
}

export class EventSaveOrderLogistic {
    constructor(logistic, booking, currency) {
        this.name = 'save_order_logistic';
        this.providerVisible = false;

        const order = booking.order;
        const items = order.items.map(item => {
            return {
                item_id: item.id,
                item_name: item.title,
                price: item.price,
                quantity: item.count,
                is_renting: item.is_renting
            }
        })
        this.parameters = {
            currency: currency,
            value: order.total,
            items: items,
            order_id: order.id,
            order_status: order.status,
            booking_id: booking.id,
            customer_id: order.customer_id,
            logistic_id: logistic.id,
            logistic_status: logistic.status,
            logistic_trade_no: logistic.trade_no
        };
    }
}

// Booking Filter
export class EventApplyBookingFilters {
    constructor(filtersInEffect) {
        this.name = 'apply_booking_filters';
        this.providerVisible = false;
        this.parameters = {
            has_start: !!filtersInEffect.datetimeStart,
            has_end: !!filtersInEffect.datetimeEnd,
            has_payment_condition: !!filtersInEffect.paymentCondition,
            has_logistic_condition: !!filtersInEffect.logisticCondition,
            selected_service_count: JSON.parse('[' + filtersInEffect.serviceIds + ']').length,
            selected_booking_pass_count: JSON.parse('[' + filtersInEffect.bookingPassIds + ']').length,
            selected_member_count: JSON.parse('[' + filtersInEffect.memberIds + ']').length,
            selected_logistic: JSON.parse('[' + filtersInEffect.logisticIds + ']').length
        };
    }
}

export class EventClearAllBookingFilters {
    constructor() {
        this.name = 'clear_all_booking_filters';
        this.providerVisible = false;
        this.parameters = {};
    }
}

export class EventClearBookingFilter {
    constructor(filterType) {
        this.name = 'clear_booking_filter';
        this.providerVisible = false;
        this.parameters = {
            filter_type: filterType
        };
    }
}

export class EventViewFilteredServiceItems {
    constructor(services) {
        this.name = 'view_filtered_service_items';
        this.providerVisible = false;
        const serviceIds = services.map(service => service.id);
        this.parameters = {
            service_ids: serviceIds
        };
    }
}

export class EventViewFilteredBookingPass {
    constructor(bookingPasses) {
        this.name = 'view_filtered_booking_passes';
        this.providerVisible = false;
        const bookingPassIds = bookingPasses.map(bookingPass => bookingPass.id);
        this.parameters = {
            booking_pass_ids: bookingPassIds
        };
    }
}

// Customer
export class EventCreateCustomer {
    constructor(isSuccess) {
        this.name = 'create_customer';
        this.providerVisible = false;
        this.parameters = {
            is_success: isSuccess
        };
    }
}

export class EventSaveCustomerBasicInformation {
    constructor(customerId, isSuccess) {
        this.name = 'save_customer_basic_information';
        this.providerVisible = false;
        this.parameters = {
            customer_id: customerId,
            save_method: customerId === -1 ? 'create' : 'update',
            is_success: isSuccess
        };
    }
}

export class EventSaveCustomerLevel {
    constructor(customer) {
        this.name = 'save_customer_level';
        this.providerVisible = false;
        this.parameters = {
            customer_id: customer.id,
            level_id: customer.level_id
        };
    }
}

export class EventSaveCustomerTag {
    constructor(customer) {
        this.name = 'save_customer_tag';
        this.providerVisible = false;
        this.parameters = {
            customer_id: customer.id,
            tags: customer.tags
        };
    }
}

export class EventSaveCustomerAddress {
    constructor(customer) {
        this.name = 'save_customer_address';
        this.providerVisible = false;
        this.parameters = {
            customer_id: customer.id
        };
    }
}

export class EventDeleteCustomerAddress {
    constructor(customer) {
        this.name = 'delete_customer_address';
        this.providerVisible = false;
        this.parameters = {
            customer_id: customer.id
        };
    }
}

export class EventSaveCustomerDate {
    constructor(customer) {
        this.name = 'save_customer_date';
        this.providerVisible = false;
        this.parameters = {
            customer_id: customer.id
        };
    }
}

export class EventDeleteCustomerDate {
    constructor(customer) {
        this.name = 'delete_customer_date';
        this.providerVisible = false;
        this.parameters = {
            customer_id: customer.id
        };
    }
}

export class EventUpdateCustomerNote {
    constructor(customer) {
        this.name = 'update_customer_note';
        this.providerVisible = false;
        this.parameters = {
            customer_id: customer.id
        };
    }
}

export class EventUpdateCustomerNoteToCustomer {
    constructor(customer) {
        this.name = 'update_customer_note_to_customer';
        this.providerVisible = false;
        this.parameters = {
            customer_id: customer.id
        };
    }
}

export class EventVerifyCustomerSms {
    constructor(customer) {
        this.name = 'verify_customer_sms';
        this.providerVisible = false;
        this.parameters = {
            customer_id: customer.id
        };
    }
}

export class EventDeleteCustomer {
    constructor(customer) {
        this.name = 'delete_customer';
        this.providerVisible = false;
        this.parameters = {
            customer_id: customer.id
        };
    }
}

// Customer Fast-editing
export class EventToggleCustomerFastEditing {
    constructor(isFastEditing) {
        this.name = 'toggle_customer_fast_editing';
        this.providerVisible = false;
        this.parameters = {
            is_fast_editing: isFastEditing
        };
    }
}

export class EventFastEditCustomerLevel {
    constructor(customerId, level, method) {
        this.name = 'fast_edit_customer_level';
        this.providerVisible = false;
        this.parameters = {
            customer_id: customerId,
            level_id: level.id,
            edit_method: method
        };
    }
}

export class EventFastEditCustomerTag {
    constructor(customerId, tag, method) {
        this.name = 'fast_edit_customer_tag';
        this.providerVisible = false;
        this.parameters = {
            customer_id: customerId,
            tag_id: tag.id,
            edit_method: method
        };
    }
}

export class EventFastEditCustomerNote {
    constructor(customer) {
        this.name = 'fast_edit_customer_note';
        this.providerVisible = false;
        this.parameters = {
            customer_id: customer.id
        };
    }
}

export class EventClickCustomerMoreAction {
    constructor(customer, type) {
        this.name = 'click_customer_more_action';
        this.providerVisible = false;
        this.parameters = {
            customer_id: customer.id,
            more_action_type: type
        };
    }
}

export class EventClickCustomerGroupMoreAction {
    constructor(type, count) {
        this.name = 'click_customer_group_more_action';
        this.providerVisible = false;
        this.parameters = {
            more_action_type: type,
            customers_count: count
        };
    }
}

// Customer Filter
export class EventApplyCustomerFilters {
    constructor(query) {
        this.name = 'apply_customer_filters';
        this.providerVisible = false;
        this.parameters = {
            selected_levels_count: query.levels.length,
            selected_tags_count: query.tags.length,
            selected_date_count: query.dateQueries.length,
            has_period: !!query.history.rangeStart && !!query.history.rangeEnd,
            has_max_count: query.history.maximalCount > 0,
            has_min_count: query.history.minimalCount > 0,
            selected_members_count: query.history.memberIds.length,
            selected_services_count: query.history.serviceIds.length,
            selected_buyables_count: query.history.buyables.length,
            has_max_amount: query.history.maximalAmount > 0,
            has_min_amount: query.history.minimalAmount > 0,
            has_keywords: !!query.keywords
        };
    }
}

export class EventClearAllCustomerFilters {
    constructor() {
        this.name = 'clear_all_customer_filters';
        this.providerVisible = false;
        this.parameters = {};
    }
}

export class EventRemoveCustomerFilter {
    constructor(type) {
        this.name = 'remove_customer_filter';
        this.providerVisible = false;
        this.parameters = {
            filter_type: type
        };
    }
}

export class EventSwitchCustomerFilterServiceType {
    constructor(type) {
        this.name = 'switch_customer_filter_services_type';
        this.providerVisible = false;
        this.parameters = {
            current_type: type
        };
    }
}

// Customer Point Module
export class EventSaveCustomerPointsModule {
    constructor(status, rateDollar, ratePoint) {
        this.name = 'save_customer_points_module';
        this.providerVisible = false;
        this.parameters = {
            status,
            rate_dollar: rateDollar,
            rate_point: ratePoint,
        };
    }
}

// Blocked Customer
export class EventSaveBlockedCustomer {
    constructor() {
        this.name = 'block_customer';
        this.providerVisible = false;
        this.parameters = {};
    }
}

export class EventDeleteBlockedCustomer{
    constructor() {
        this.name = 'unblock_customer';
        this.providerVisible = false;
        this.parameters = {};
    }
}

// Provider Tag
export class EventSaveProviderTag {
    constructor(tag) {
        this.name = 'save_provider_tag';
        this.providerVisible = false;
        this.parameters = {
            save_type: tag.id ? 'update' : 'create',
            tag_id: tag.id
        };
    }
}

export class EventDeleteProviderTag {
    constructor(tag) {
        this.name = 'delete_provider_tag';
        this.providerVisible = false;
        this.parameters = {
            tag_id: tag.id
        };
    }
}

// Provider Level
export class EventSaveProviderLevel {
    constructor(level) {
        this.name = 'save_provider_level';
        this.providerVisible = false;
        this.parameters = {
            save_type: level.id ? 'update' : 'create',
            level_id: level.id
        };
    }
}

export class EventDeleteProviderLevel {
    constructor(level) {
        this.name = 'delete_provider_level';
        this.providerVisible = false;
        this.parameters = {
            level_id: level.id
        };
    }
}

//  Contract
export class EventPreviewContract {
    constructor(contract) {
        this.name = 'preview_contract';
        this.providerVisible = false;
        this.parameters = {
            booking_id: contract.booking_id,
            contract_id: contract.id,
            contract_status: contract.status
        };
    }
}

export class EventSaveContract {
    constructor(contract, isSuccess) {
        this.name = 'save_contract';
        this.providerVisible = false;
        this.parameters = {
            booking_id: contract.booking_id,
            contract_id: contract.id,
            save_method: contract.id ? 'update' : 'create',
            is_success: isSuccess
        };
    }
}

export class EventSendContract {
    constructor(contract, isSuccess) {
        this.name = 'send_contract';
        this.providerVisible = false;
        this.parameters = {
            booking_id: contract.booking_id,
            contract_id: contract.id,
            is_success: isSuccess
        };
    }
}

export class EventCreateNewContractVersion {
    constructor(booking) {
        this.name = 'create_new_contract_version';
        this.providerVisible = false;
        this.parameters = {
            booking_id: booking.id
        };
    }
}

// Email Module
export class EventSendEmail {
    constructor(booking, customer, customerQuery, isSuccess) {
        this.name = 'send_email';
        this.providerVisible = false;

        const parameters = {
            is_success: isSuccess
        };
        if (booking) {
            parameters.booking_id = booking.id;
            parameters.customer_id = booking.customer_id;
            parameters.email_type = 'to_booking';
        } else if (customer) {
            parameters.customer_id = customer.id;
            parameters.email_type = 'to_customer';
        } else if (customerQuery) {
            parameters.email_type = 'to_customers';
        } else {
            parameters.email_type = 'to_bookings';
        }
        this.parameters = parameters;
    }
}

export class EventSaveEmailTemplate {
    constructor(template, booking, customer, customerQuery, isSuccess) {
        this.name = 'save_email_template';
        this.providerVisible = false;

        const parameters = {
            email_template_id: template.id,
            save_method: template.id ? 'update' : 'create',
            is_success: isSuccess
        };
        if (booking) {
            parameters.booking_id = booking.id;
            parameters.customer_id = booking.customer_id;
            parameters.email_type = 'to_booking';
        } else if (customer) {
            parameters.customer_id = customer.id;
            parameters.email_type = 'to_customer';
        } else if (customerQuery) {
            parameters.email_type = 'to_customers';
        } else {
            parameters.email_type = 'to_bookings';
        }
        this.parameters = parameters;
    }
}

export class EventApplyEmailTemplate {
    constructor(template, booking, customer, customerQuery) {
        this.name = 'apply_email_template';
        this.providerVisible = false;

        const parameters = {
            email_template_id: template.id
        };
        if (booking) {
            parameters.booking_id = booking.id;
            parameters.customer_id = booking.customer_id;
            parameters.email_type = 'to_booking';
        } else if (customer) {
            parameters.customer_id = customer.id;
            parameters.email_type = 'to_customer';
        } else if (customerQuery) {
            parameters.email_type = 'to_customers';
        } else {
            parameters.email_type = 'to_bookings';
        }
        this.parameters = parameters;
    }
}

export class EventDeleteEmailTemplate {
    constructor(template, booking, customer, customerQuery) {
        this.name = 'delete_email_template';
        this.providerVisible = false;

        const parameters = {
            email_template_id: template.id
        };
        if (booking) {
            parameters.booking_id = booking.id;
            parameters.customer_id = booking.customer_id;
            parameters.email_type = 'to_booking';
        } else if (customer) {
            parameters.customer_id = customer.id;
            parameters.email_type = 'to_customer';
        } else if (customerQuery) {
            parameters.email_type = 'to_customers';
        } else {
            parameters.email_type = 'to_bookings';
        }
        this.parameters = parameters;
    }
}

// SMS Module
export class EventSendSms {
    constructor(booking, customer, customerQuery, isSuccess) {
        this.name = 'send_sms';
        this.providerVisible = false;

        const parameters = {
            is_success: isSuccess
        };
        if (booking) {
            parameters.booking_id = booking.id;
            parameters.customer_id = booking.customer_id;
            parameters.sms_type = 'to_booking';
        } else if (customer) {
            parameters.customer_id = customer.id;
            parameters.sms_type = 'to_customer';
        } else if (customerQuery) {
            parameters.sms_type = 'to_customers';
        } else {
            parameters.sms_type = 'to_bookings';
        }
        this.parameters = parameters;
    }
}

export class EventSaveSmsTemplate {
    constructor(template, booking, customer, customerQuery, isSuccess) {
        this.name = 'save_sms_template';
        this.providerVisible = false;

        const parameters = {
            sms_template_id: template.id,
            save_method: template.id ? 'update' : 'create',
            is_success: isSuccess
        };
        if (booking) {
            parameters.booking_id = booking.id;
            parameters.customer_id = booking.customer_id;
            parameters.sms_type = 'to_booking';
        } else if (customer) {
            parameters.customer_id = customer.id;
            parameters.sms_type = 'to_customer';
        } else if (customerQuery) {
            parameters.sms_type = 'to_customers';
        } else {
            parameters.sms_type = 'to_bookings';
        }
        this.parameters = parameters;
    }
}

export class EventApplySmsTemplate {
    constructor(template, booking, customer, customerQuery) {
        this.name = 'apply_sms_template';
        this.providerVisible = false;

        const parameters = {
            sms_template_id: template.id
        };
        if (booking) {
            parameters.booking_id = booking.id;
            parameters.customer_id = booking.customer_id;
            parameters.sms_type = 'to_booking';
        } else if (customer) {
            parameters.customer_id = customer.id;
            parameters.sms_type = 'to_customer';
        } else if (customerQuery) {
            parameters.sms_type = 'to_customers';
        } else {
            parameters.sms_type = 'to_bookings';
        }
        this.parameters = parameters;
    }
}

export class EventDeleteSmsTemplate {
    constructor(template, booking, customer, customerQuery) {
        this.name = 'delete_sms_template';
        this.providerVisible = false;

        const parameters = {
            sms_template_id: template.id
        };
        if (booking) {
            parameters.booking_id = booking.id;
            parameters.customer_id = booking.customer_id;
            parameters.sms_type = 'to_booking';
        } else if (customer) {
            parameters.customer_id = customer.id;
            parameters.sms_type = 'to_customer';
        } else if (customerQuery) {
            parameters.sms_type = 'to_customers';
        } else {
            parameters.sms_type = 'to_bookings';
        }
        this.parameters = parameters;
    }
}

// File Vault Module
export class EventViewFileVault {
    constructor(fileVault, booking) {
        this.name = 'view_file_vault';
        this.providerVisible = false;
        this.parameters = {
            file_vault_id: fileVault.id,
            booking_id: booking.id,
            booking_status: booking.status
        };
    }
}

export class EventCreateFileVaultFolder {
    constructor(fileVault) {
        this.name = 'create_file_vault_folder';
        this.providerVisible = false;
        this.parameters = {
            file_vault_id: fileVault.id
        };
    }
}

export class EventUploadFileInFileVault {
    constructor(file, fileVault) {
        this.name = 'upload_file_in_file_vault';
        this.providerVisible = false;
        this.parameters = {
            file_vault_id: fileVault.id,
            file_type: file.type
        };
    }
}

export class EventSwitchFileVaultView {
    constructor(viewType, fileVault) {
        this.name = 'switch_file_vault_view';
        this.providerVisible = false;
        this.parameters = {
            file_vault_id: fileVault.id,
            view_type: viewType
        };
    }
}

export class EventBeginShareFileVault {
    constructor(fileVault, booking) {
        this.name = 'begin_share_file_vault';
        this.providerVisible = false;
        this.parameters = {
            file_vault_id: fileVault.id,
            booking_id: booking.id,
            booking_status: booking.status
        };
    }
}

export class EventCopyFileVaultShareLink {
    constructor(fileVault) {
        this.name = 'copy_file_vault_share_link';
        this.providerVisible = false;
        this.parameters = {
            file_vault_id: fileVault.id
        };
    }
}

export class EventSendFileVaultInvitation {
    constructor(fileVault, isSuccess) {
        this.name = 'send_file_vault_invitation';
        this.providerVisible = false;
        this.parameters = {
            file_vault_id: fileVault.id,
            is_success: isSuccess
        };
    }
}

export class EventClickFileVaultMoreAction {
    constructor(type, file, fileVault) {
        this.name = 'click_file_vault_more_action';
        this.providerVisible = false;
        this.parameters = {
            more_action_type: type,
            file_type: file.type,
            file_vault_id: fileVault.id
        };
    }
}

export class EventRenameFileVaultFile {
    constructor(file, fileVault) {
        this.name = 'rename_file_vault_file';
        this.providerVisible = false;
        this.parameters = {
            file_id: file.id,
            file_type: file.type,
            file_vault_id: fileVault.id
        };
    }
}

export class EventDeleteFileVaultFile {
    constructor(file, fileVault) {
        this.name = 'delete_file_vault_file';
        this.providerVisible = false;
        this.parameters = {
            file_id: file.id,
            file_type: file.type,
            file_vault_id: fileVault.id
        };
    }
}

// Studio
export class EventSaveProviderBasicInformation {
    constructor() {
        this.name = 'save_provider_basic_information';
        this.providerVisible = false;
        this.parameters = {};
    }
}

export class EventSaveProviderWorkingTime {
    constructor() {
        this.name = 'save_provider_working_time';
        this.providerVisible = false;
        this.parameters = {};
    }
}

export class EventSaveEcpayGateway {
    constructor(methods) {
        this.name = 'save_ecpay_gateway';
        this.providerVisible = false;
        this.parameters = {
            ecpay_payment_methods: methods
        };
    }
}

export class EventSaveLinepayGateway {
    constructor() {
        this.name = 'save_linepay_gateway';
        this.providerVisible = false;
        this.parameters = {};
    }
}

export class EventSaveTappayGateway {
    constructor() {
        this.name = 'save_tappay_gateway';
        this.providerVisible = false;
        this.parameters = {};
    }
}

export class EventRemoveGateway {
    constructor(type) {
        this.name = 'remove_gateway';
        this.providerVisible = false;
        this.parameters = {
            gateway_type: type
        };
    }
}

export class EventSaveProviderPaymentSetting {
    constructor() {
        this.name = 'save_provider_payment_setting';
        this.providerVisible = false;
        this.parameters = {};
    }
}

// Customer Notification
export class EventToggleEmailNotificationActivation {
    constructor(isEnabled) {
        this.name = 'toggle_email_notification_activation';
        this.providerVisible = false;
        this.parameters = {
            is_enabled: isEnabled
        };
    }
}

export class EventToggleSmsNotificationActivation {
    constructor(isEnabled) {
        this.name = 'toggle_sms_notification_activation';
        this.providerVisible = false;
        this.parameters = {
            is_enabled: isEnabled
        };
    }
}

export class EventToggleFBNotificationActivation {
    constructor(isEnabled) {
        this.name = 'toggle_fb_notification_activation';
        this.providerVisible = false;
        this.parameters = {
            is_enabled: isEnabled
        };
    }
}

export class EventToggleLineMessageNotificationActivation {
    constructor(isEnabled) {
        this.name = 'toggle_line_message_notification_activation';
        this.providerVisible = false;
        this.parameters = {
            is_enabled: isEnabled
        };
    }
}

// Sms
export class EventSwitchSmsRecordTab {
    constructor(tab) {
        this.name = 'switch_sms_record_tab';
        this.providerVisible = false;
        this.parameters = {
            current_tab: tab
        };
    }
}

export class EventViewSmsRate {
    constructor() {
        this.name = 'view_sms_rate';
        this.providerVisible = false;
        this.parameters = {};
    }
}

export class EventViewSmsContent {
    constructor(record) {
        this.name = 'view_sms_content';
        this.providerVisible = false;
        this.parameters = {
            sms_id: record.id,
            sms_status: record.status,
            booking_id: record.booking_id,
            customer_id: record.customer_id
        };
    }
}

export class EventResendSms {
    constructor(record, isSuccess) {
        this.name = 'resend_sms';
        this.providerVisible = false;
        this.parameters = {
            sms_id: record.id,
            sms_status: record.status,
            booking_id: record.booking_id,
            customer_id: record.customer_id,
            is_success: isSuccess
        };
    }
}

export class EventDepositSmsCredit {
    constructor(amount) {
        this.name = 'deposit_sms_credit';
        this.providerVisible = false;
        this.parameters = {
            amount: amount
        };
    }
}

export class EventToggleBookingStatusChangeActivation {
    constructor(isEnabled) {
        this.name = 'toggle_booking_status_change_activation';
        this.providerVisible = false;
        this.parameters = {
            is_enabled: isEnabled
        };
    }
}

export class EventToggleBookingReminderActivation {
    constructor(isEnabled) {
        this.name = 'toggle_booking_reminder_activation';
        this.providerVisible = false;
        this.parameters = {
            is_enabled: isEnabled
        };
    }
}

export class EventTogglePaymentRequestActivation {
    constructor(isEnabled) {
        this.name = 'toggle_payment_request_activation';
        this.providerVisible = false;
        this.parameters = {
            is_enabled: isEnabled
        };
    }
}

export class EventToggleSendCouponActivation {
    constructor(isEnabled) {
        this.name = 'toggle_send_coupon_activation';
        this.providerVisible = false;
        this.parameters = {
            is_enabled: isEnabled
        };
    }
}

export class EventTogglePaymentWillExpireReminder {
    constructor(isEnabled) {
        this.name = 'toggle_payment_will_expire_reminder';
        this.providerVisible = false;
        this.parameters = {
            is_enabled: isEnabled
        };
    }
}

// Member
export class EventDeleteMember {
    constructor(member, isSuccess) {
        this.name = 'delete_member';
        this.providerVisible = false;
        this.parameters = {
            member_id: member.id,
            is_success: isSuccess
        };
    }
}

export class EventRestoreMember {
    constructor(member) {
        this.name = 'restore_member';
        this.providerVisible = false;
        this.parameters = {
            member_id: member.id
        };
    }
}

export class EventSaveMember {
    constructor(member, isSuccess) {
        this.name = 'save_member';
        this.providerVisible = false;
        this.parameters = {
            member_id: member.id,
            save_method: member.id ? 'update' : 'create',
            is_success: isSuccess
        };
    }
}

export class EventClickApplyProviderWorkingTime {
    constructor() {
        this.name = 'click_apply_provider_working_time';
        this.providerVisible = false;
        this.parameters = {};
    }
}

// Service
export class EventSearchService {
    constructor(keywords) {
        this.name = 'search_service';
        this.providerVisible = false;
        this.parameters = {
            search_term: keywords
        };
    }
}

export class EventSwitchServiceTab {
    constructor(tab) {
        this.name = 'switch_service_tab';
        this.providerVisible = false;
        this.parameters = {
            current_tab: tab
        };
    }
}

export class EventSaveServiceOrder {
    constructor() {
        this.name = 'save_services_order';
        this.providerVisible = false;
        this.parameters = {};
    }
}

export class EventSaveService {
    constructor(service, isSuccess) {
        this.name = 'save_service';
        this.providerVisible = false;
        this.parameters = {
            service_id: service.id,
            save_method: service.id ? 'update' : 'create',
            is_success: isSuccess
        };
    }
}

export class EventPreviewForm {
    constructor(form, service) {
        this.name = 'preview_form';
        this.providerVisible = false;
        this.parameters = {
            service_id: service.id,
            form_id: form.id
        };
    }
}

export class EventDuplicateService {
    constructor(service, isSuccess) {
        this.name = 'duplicate_service';
        this.providerVisible = false;
        this.parameters = {
            service_id: service.id,
            is_success: isSuccess
        };
    }
}

export class EventDeleteService {
    constructor(service) {
        this.name = 'delete_service';
        this.providerVisible = false;
        this.parameters = {
            service_id: service.id
        };
    }
}

export class EventArchiveService {
    constructor(service, isSuccess) {
        this.name = 'archive_service';
        this.providerVisible = false;
        this.parameters = {
            service_id: service.id,
            is_success: isSuccess
        };
    }
}

export class EventUnarchiveService {
    constructor(service, isSuccess) {
        this.name = 'unarchive_service';
        this.providerVisible = false;
        this.parameters = {
            service_id: service.id,
            is_success: isSuccess
        };
    }
}

export class EventDisableService {
    constructor(service, isSuccess) {
        this.name = 'disable_service';
        this.providerVisible = false;
        this.parameters = {
            service_id: service.id,
            is_success: isSuccess
        };
    }
}

export class EventEnableService {
    constructor(service, isSuccess) {
        this.name = 'enable_service';
        this.providerVisible = false;
        this.parameters = {
            service_id: service.id,
            is_success: isSuccess
        };
    }
}

export class EventToggleAutoServiceScheduling {
    constructor(service, scheduleTo, type) {
        this.name = 'toggle_auto_service_scheduling';
        this.providerVisible = false;
        this.parameters = {
            service_id: service.id,
            schedule_to: scheduleTo,
            schedule_type: type
        };
    }
}

// Items
export class EventViewItemsSearchResults {
    constructor(searchTerms) {
        this.name = 'view_items_search_results';
        this.providerVisible = false;
        this.parameters = {
            search_terms: searchTerms
        };
    }
}

export class EventClickItemCategoryFilter {
    constructor(category, isEnabled) {
        this.name = 'click_item_category_filter';
        this.providerVisible = false;
        this.parameters = {
            item_category_id: category.id,
            is_enabled: isEnabled
        };
    }
}

export class EventDownloadItemsReport {
    constructor() {
        this.name = 'download_items_report';
        this.providerVisible = false;
        this.parameters = {};
    }
}

export class EventSaveProviderItemCategory {
    constructor(itemCategory) {
        this.name = 'save_provider_item_category';
        this.providerVisible = false;
        this.parameters = {
            item_category_id: itemCategory.id,
            save_method: itemCategory.id ? 'update' : 'create'
        };
    }
}

export class EventDeleteProviderItemCategory {
    constructor(itemCategory) {
        this.name = 'delete_provider_item_category';
        this.providerVisible = false;
        this.parameters = {
            item_category_id: itemCategory.id
        };
    }
}

export class EventSaveItem {
    constructor(item, isSuccess) {
        this.name = 'save_item';
        this.providerVisible = false;
        this.parameters = {
            item_id: item.id,
            save_method: item.id ? 'update' : 'create',
            is_success: isSuccess
        };
    }
}

export class EventDuplicateItem {
    constructor(item, isSuccess) {
        this.name = 'duplicate_item';
        this.providerVisible = false;
        this.parameters = {
            item_id: item.id,
            is_success: isSuccess
        };
    }
}

export class EventDuplicateItemSpec {
    constructor() {
        this.name = 'duplicate_item_spec';
        this.providerVisible = false;
        this.parameters = {};
    }
}

export class EventDeleteItem {
    constructor(item) {
        this.name = 'delete_item';
        this.providerVisible = false;
        this.parameters = {
            item_id: item.id
        };
    }
}

export class EventEnableItem {
    constructor(item) {
        this.name = 'enable_item';
        this.providerVisible = false;
        this.parameters = {
            item_id: item.id
        };
    }
}

export class EventDisableItem {
    constructor(item) {
        this.name = 'disable_item';
        this.providerVisible = false;
        this.parameters = {
            item_id: item.id
        };
    }
}

export class EventToggleAutoItemScheduling {
    constructor(item, scheduleTo, type) {
        this.name = 'toggle_auto_item_scheduling';
        this.providerVisible = false;
        this.parameters = {
            item_id: item.id,
            schedule_to: scheduleTo,
            schedule_type: type
        };
    }
}

// Pricing Rules
export class EventSavePricingRules {
    constructor(entityType) {
        this.name = 'save_pricing_rules';
        this.providerVisible = false;
        this.parameters = {
            entity_type: entityType
        };
    }
}

export class EventDeletePricingRules{
    constructor() {
        this.name = 'delete_pricing_rules';
        this.providerVisible = false;
        this.parameters = {};
    }
}

// Offer
export class EventSaveOffer {
    constructor(offer, isSuccess) {
        this.name = 'save_offer';
        this.providerVisible = false;
        this.parameters = {
            offer_id: offer.id,
            activated: !!offer.enabled,
            is_success: isSuccess,
            save_method: offer.id ? 'update' : 'create'
        };
    }
}

export class EventDeleteOffer {
    constructor(offer) {
        this.name = 'delete_offer';
        this.providerVisible = false;
        this.parameters = {
            offer_id: offer.id
        };
    }
}

export class EventSwitchOfferTab {
    constructor(currentTab) {
        this.name = 'switch_offer_tab';
        this.providerVisible = false;
        this.parameters = {
            current_tab: currentTab
        }
    }
}

export class EventArchiveOffer {
    constructor(offer, isSuccess) {
        this.name = 'archive_offer';
        this.providerVisible = false;
        this.parameters = {
            offer_id: offer.id,
            is_success: isSuccess
        };
    }
}

export class EventSwitchOfferModuleEnabled {
    constructor(enabled) {
        this.name = 'switch_offer_module_enabled';
        this.providerVisible = false;
        this.parameters = {
            enabled: enabled
        }
    }
}

export class EventUnarchiveOffer {
    constructor(offer, isSuccess) {
        this.name = 'unarchive_offer';
        this.providerVisible = false;
        this.parameters = {
            offer_id: offer.id,
            is_success: isSuccess
        };
    }
}

// Logistics
export class EventSaveLogistic {
    constructor(logistic, isSuccess) {
        this.name = 'save_logistic';
        this.providerVisible = false;
        this.parameters = {
            logistic_id: logistic.id,
            is_success: isSuccess,
            save_method: logistic.id ? 'update' : 'create'
        };
    }
}

export class EventDeleteLogistic {
    constructor(logistic) {
        this.name = 'delete_logistic';
        this.providerVisible = false;
        this.parameters = {
            logistic_id: logistic.id
        };
    }
}

export class EventSaveLogisticsOrder {
    constructor() {
        this.name = 'save_logistics_order';
        this.providerVisible = false;
        this.parameters = {};
    }
}

// Booking Pass
export class EventSwitchBookingPassTab {
    constructor(tab) {
        this.name = 'switch_booking_pass_tab';
        this.providerVisible = false;
        this.parameters = {
            current_tab: tab
        };
    }
}

export class EventArchiveBookingPass {
    constructor() {
        this.name = 'archive_booking_pass';
        this.providerVisible = false;
        this.parameters = {};
    }
}

export class EventSaveBookingPass {
    constructor(bookingPass, isSuccess) {
        this.name = 'save_booking_pass';
        this.providerVisible = false;
        this.parameters = {
            booking_pass_id: bookingPass.id,
            activated: !!bookingPass.enabled_at,
            is_success: isSuccess,
            save_method: bookingPass.id ? 'update' : 'create'
        };
    }
}

// Contracts
export class EventCreateContractTemplate {
    constructor(isSuccess) {
        this.name = 'create_contract_template';
        this.providerVisible = false;
        this.parameters = {
            is_success: isSuccess
        };
    }
}

export class EventSaveContractTemplate {
    constructor(contract, isSuccess) {
        this.name = 'save_contract_template';
        this.providerVisible = false;
        this.parameters = {
            contract_id: contract.id,
            is_success: isSuccess
        };
    }
}

export class EventDeleteContractTemplate {
    constructor(contract) {
        this.name = 'delete_contract_template';
        this.providerVisible = false;
        this.parameters = {
            contract_id: contract.id
        };
    }
}

export class EventRenameContractTemplate {
    constructor(contract) {
        this.name = 'rename_contract_template';
        this.providerVisible = false;
        this.parameters = {
            contract_id: contract.id
        };
    }
}

export class EventUploadESigning {
    constructor() {
        this.name = 'upload_e_signing';
        this.providerVisible = false;
        this.parameters = {};
    }
}

// Questions
export class EventAddQuestion {
    constructor(form, question) {
        this.name = 'add_question';
        this.providerVisible = false;
        this.parameters = {
            form_id: form.id,
            question_type: question.answer_type
        };
    }
}

export class EventUpdateQuestion {
    constructor(form, question) {
        this.name = 'update_question';
        this.providerVisible = false;
        this.parameters = {
            form_id: form.id,
            question_type: question.answer_type
        };
    }
}

// Landing
export class EventSaveDisplaySetting {
    constructor() {
        this.name = 'save_display_setting';
        this.providerVisible = false;
        this.parameters = {};
    }
}

export class EventAddBlock {
    constructor(block) {
        this.name = 'add_block';
        this.providerVisible = false;
        this.parameters = {
            block_type: block.content_render_type
        };
    }
}

export class EventUpdateBlock {
    constructor(block) {
        this.name = 'update_block';
        this.providerVisible = false;
        this.parameters = {
            block_type: block.content_render_type
        };
    }
}

// Subscription
export class EventClickContactDing {
    constructor() {
        this.name = 'click_contact_ding';
        this.providerVisible = false;
        this.parameters = {};
    }
}

// Landing page --------------------------------------------------------------------------------------
// e-commerce
export class EventAddToCart {
    constructor(currency, value, items) {
        this.name = 'add_to_cart';
        this.providerVisible = true;

        this.parameters = {
            currency : currency,
            value: value,
            items: items
        };
    }
}

export class EventRemoveFromCart {
    constructor(item, count, spec) {
        this.name = 'remove_from_cart';
        this.providerVisible = true;

        let value = 0;
        let items = [];
        if (!spec) {
            const itemData = Object.assign({}, {
                item_id: item.id,
                item_name: item.name,
                quantity: count
            })

            if (item.is_renting) {
                value = parseInt(item.renting_charge_amount) * count;
                itemData.price = parseInt(item.renting_charge_amount);
            } else {
                value = parseInt(item.price) * count;
                itemData.price = parseInt(item.price);
            }
            items.push(itemData);
        } else {
            value = parseInt(spec.price) * count;
            const itemData = Object.assign({}, {
                item_id: item.id,
                item_name: item.name + '/' + spec.name,
                price: parseInt(spec.price),
                quantity: count,
                spec_id: spec.id
            })
            items.push(itemData);
        }
        this.parameters = {
            value: value,
            items: items
        };
    }
}

export class EventViewItem {
    constructor(currency, item) {
        this.name = 'view_item';
        this.providerVisible = true;

        let value = 0;
        let items = [];
        if (!item.specs.length) {
            const itemData = Object.assign({}, {
                item_id: item.id,
                item_name: item.name,
                quantity: 1
            })
            if (item.is_renting) {
                value = parseInt(item.renting_charge_amount);
                itemData.price = parseInt(item.renting_charge_amount);
            } else {
                value = parseInt(item.price);
                itemData.price = parseInt(item.price);
            }
            items.push(itemData);
        } else {
            for(var i in item.specs) {
                const spec = item.specs[i];
                const specPrice = parseInt(item.is_renting ? spec.renting_charge_amount : spec.price);
                value = value + specPrice;
                items.push({
                    item_id: item.id,
                    item_name: item.name + '/' + spec.name,
                    price: parseInt(specPrice),
                    quantity: 1,
                    spec_id: spec.id
                })
            }
        }
        this.parameters = {
            currency : currency,
            value: value,
            items: items
        };
    }
}

export class EventApplyOffer {
    constructor(offer, discount, isSuccess) {
        this.name = 'apply_offer';
        this.providerVisible = true;

        this.parameters = {
            offer_id: offer.id,
            discount: discount,
            promo_code: offer.promo_code,
            is_success: isSuccess
        };
    }
}
export class EventTrackOrder {
    constructor() {
        this.name = 'track_order';
        this.providerVisible = true;
        this.parameters = {};
    }
}

export class EventViewPortfolioAlbum {
    constructor(album) {
        this.name = 'view_portfolio_album';
        this.providerVisible = true;
        this.parameters = {
            album_id: album.meta.id,
            album_title: album.meta.title
        };
    }
}

export class EventViewPortfolioAlbumImage {
    constructor(albumId, img) {
        this.name = 'view_portfolio_album_image';
        this.providerVisible = true;
        this.parameters = {
            album_id: albumId,
            image_url: img?.file_name
        };
    }
}

export class EventClickSocialLink {
    constructor(type) {
        this.name = 'click_social_link';
        this.providerVisible = true;
        this.parameters = {
            social_type: type
        };
    }
}

export class EventClickUserReview {
    constructor() {
        this.name = 'click_user_review';
        this.providerVisible = true;
        this.parameters = {};
    }
}

export class EventSelectService {
    constructor(service) {
        this.name = 'select_service';
        this.providerVisible = true;
        this.parameters = {
            service_id: service?.id,
            service_title: service?.title
        };
    }
}

export class EventClickBookableItem {
    constructor(bookable) {
        this.name = 'click_bookable_item';
        this.providerVisible = true;
        this.parameters = {
            service_id: bookable.service_id,
            service_title: bookable.name
        };
    }
}

export class EventSelectMember {
    constructor(member) {
        this.name = 'select_member';
        this.providerVisible = true;
        this.parameters = {
            member_id: member?.id,
            member_name: member?.name
        };
    }
}

export class EventUploadFile {
    constructor() {
        this.name = 'upload_file';
        this.providerVisible = true;
        this.parameters = {};
    }
}

export class EventBook {
    constructor(serviceId, memberId, start, end, isSuccess) {
        this.name = 'book';
        this.providerVisible = true;
        this.parameters = {
            member_user_id: memberId,
            service_id: serviceId,
            start_datetime: start,
            end_datetime: end,
            is_success: isSuccess
        };
    }
}

export class EventPurchase {
    constructor(currency, total, serviceId, memberId, start, end, orderItems, cartLogistic, cartAddress) {
        this.name = 'purchase';
        this.providerVisible = true;
        const offerItem = orderItems.find(item => !!item.offer_id);
        const customerOfferItem = orderItems.find(item => !!item.customer_offer_id);
        this.parameters = {
            currency,
            value: parseInt(total),
            has_offer_applied: !!offerItem,
            offer_id: offerItem?.offer_id,
            customer_offer_id: customerOfferItem?.customer_offer_id,
            order_items: orderItems,
            logistic_name: cartLogistic?.display_name,
            logistic_area: cartLogistic?.area,
            logistic_city: cartAddress.admin_level_1,
            logistic_address: cartAddress.admin_level_1 + cartAddress.admin_level_2 + cartAddress.admin_level_3 + cartAddress.address,
            member_user_id: memberId,
            service_id: serviceId,
            start_datetime: start,
            end_datetime: end ? end : null
        };
    }
}

export class EventLogin {
    constructor(method, isSuccess) {
        this.name = 'login';
        this.providerVisible = true;
        this.parameters = {
            method,
            is_success: isSuccess
        };
    }
}

export class EventRequestSmsVerification {
    constructor(via, isSuccess) {
        this.name = 'request_sms_verification';
        this.providerVisible = true;
        this.parameters = { via, is_success: isSuccess };
    }
}

export class EventForgetPassword {
    constructor() {
        this.name = 'forget_password';
        this.providerVisible = true;
        this.parameters = {};
    }
}

export class EventVerifySms {
    constructor(via, isSuccess) {
        this.name = 'verify_sms';
        this.providerVisible = true;
        this.parameters = { via, is_success: isSuccess };
    }
}

// Provider Campaign
export class EventSaveProviderCampaign {
    constructor(campaign, isSuccess) {
        this.name = 'save_provider_campaign';
        this.providerVisible = false;
        this.parameters = {
            campaign_id: campaign.id,
            activated: !!campaign.enabled,
            is_success: isSuccess,
            save_method: campaign.id ? 'update' : 'create'
        };
    }
}
